import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { IPersistenceTrackingClient } from '../application/store/middleware/tracking/clients/trackingClient';

/**
 * Module-level reference to the persistence tracking client.
 * Allows non-React code (like Redux thunk actions) to access tracking methods.
 */
let persistenceTrackingClientRef: IPersistenceTrackingClient | null = null;

/**
 * Gets the current learner ID for use in non-React contexts.
 * Returns 'anonymous' if the tracking client hasn't been initialized yet.
 */
export const getCurrentLearnerId = (): string => persistenceTrackingClientRef?.getLearnerId() ?? 'anonymous';

type LearnerContextProps = {
  learnerId: string;
};

const LearnerContext = createContext<LearnerContextProps | null>(null);

type LearnerContextProviderProps = {
  children: React.ReactNode;
  persistenceTrackingClient: IPersistenceTrackingClient;
};

export const LearnerContextProvider = (props: LearnerContextProviderProps) => {
  const learnerId = useMemo(() => props.persistenceTrackingClient.getLearnerId(), [props.persistenceTrackingClient]);

  useEffect(() => {
    persistenceTrackingClientRef = props.persistenceTrackingClient;
    return () => {
      persistenceTrackingClientRef = null;
    };
  }, [props.persistenceTrackingClient]);

  const value = useMemo<LearnerContextProps>(() => ({ learnerId }), [learnerId]);

  return <LearnerContext.Provider value={value}>{props.children}</LearnerContext.Provider>;
};

export const useLearnerContext = (): LearnerContextProps => {
  const context = useContext(LearnerContext);
  if (!context) {
    throw new Error('useLearnerContext must be used within a LearnerContextProvider');
  }
  return context;
};
